import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Font } from '../../../constants'
import { Callback } from '../../../modules/callback'

interface InputFieldChangeEvent {
  fieldId: string
  value: string
}

export interface LvcSelect {
  selectOptions: string
  selectOptionLabels: string
  placeholder: string
  theme: 'default' | 'outline'
}

export class LvcSelect extends MinzeElement {
  reactive: Reactive = [['value', '', true]]

  attrs: Attrs = [
    ['placeholder', ''],
    ['height', '48px'],
    'type',
    'name',
    'id',
    ['secondary-style', false],
    ['default-value', ''],
    'label',
    ['padding', '12px 16px'],
    ['fontSize', '14px'],
    ['selectOptions', []],
    ['selectOptionLabels', []],
    ['selectOptgroupLabels', ''],
    ['error', false],
    ['onChange'],
    ['theme', 'default'],
    ['font-weight', Font.weight.medium],
  ]
  heightValue = this.height

  static observedAttributes = ['height', 'default-value', 'error']

  onAttributeChange = (name: string, oldValue: string, newValue: string) => {
    if (name === 'default-value' && oldValue !== newValue) {
      this.value = newValue ?? ''

      const currentElement = this.select(`#${this.id}`)
      if (currentElement as any) {
        ;(currentElement as any).value = newValue
      }
      this.rerender(true)
    }

    this.heightValue = this.height ? this.height : '48px'
    this.rerender()
  }

  changeSelect = (event: Event) => {
    this.value = (event as any).target.value
    Callback.call(this.onChange as string, event)
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0
    let placeholder = typeof this.placeholder === 'boolean' ? '' : this.placeholder

    // Check if we have optgroups
    const hasOptgroups =
      typeof this.selectOptgroupLabels === 'string' && this.selectOptgroupLabels.length > 0

    let optionsHtml = ''
    if (
      hasOptgroups &&
      typeof this.selectOptions === 'string' &&
      typeof this.selectOptionLabels === 'string'
    ) {
      // Split options and labels
      const options = this.selectOptions.split(',')
      const labels = this.selectOptionLabels.split(',')
      const optgroups = (this.selectOptgroupLabels as string).split(',')

      // For contact form: first 4 options are B2C, remaining are B2B
      const b2cCount = 4
      const b2cOptions = options.slice(0, b2cCount)
      const b2cLabels = labels.slice(0, b2cCount)
      const b2bOptions = options.slice(b2cCount)
      const b2bLabels = labels.slice(b2cCount)

      optionsHtml = `
        <optgroup label="${optgroups[0]}">
          ${b2cOptions
            .map(
              (option, index) =>
                `<option value="${option}" ${this.value === option ? 'selected' : ''}>${b2cLabels[index]}</option>`
            )
            .join('')}
        </optgroup>
        <optgroup label="${optgroups[1]}">
          ${b2bOptions
            .map(
              (option, index) =>
                `<option value="${option}" ${this.value === option ? 'selected' : ''}>${b2bLabels[index]}</option>`
            )
            .join('')}
        </optgroup>
      `
    } else if (
      typeof this.selectOptions === 'string' &&
      this.selectOptions.length > 0 &&
      typeof this.selectOptionLabels === 'string'
    ) {
      // Original flat options
      optionsHtml = this.selectOptions
        .split(',')
        .map(
          (option: any, index) =>
            `<option value="${option}" ${this.value === option ? 'selected' : ''}>${this.selectOptionLabels.split(',')[index]}</option>`
        )
        .join('')
    }

    return `
      <lvc-box height="auto">
        ${this.label ? `<label for="${this.id}">${this.label}</label>` : ''}
        <select
          placeholder="${placeholder}"
          ${this.type ? `type="${this.type}"` : ''}
          name="${this.name}"
          id="${this.id}"
          value="${this.value}"
          class="input ${this.secondaryStyle ? 'secondary-style' : ''} ${this.showError ? 'error' : ''}"
        >
          ${placeholder ? `<option value="" ${this.value === '' ? 'selected' : ''}">${placeholder}</option>` : ''}
          ${optionsHtml}
        </select>
        ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
      </lvc-box>
    `
  }

  css = () => `
  :host {
    width: 100%;
  }

  .input:focus-visible {
    outline: unset;
  }

  .input {
    font-family: ${Font.family};
    width: 100%;
    border: unset;
    height: ${this.heightValue || '48px'};
    ${this.fontSize ? `font-size: ${this.fontSize};` : ''}
    font-size: ${this.fontSize || '14px'};
    ${this.padding ? `padding: ${this.padding};` : ''}
    background: #fff;
    color: #1E007D;
    margin-top: 4px;
    padding-left: 16px;
    padding-right: 40px;
    border-radius: 99px;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    border: none;
    background: ${this.value && this.secondaryStyle ? '#BFF0FF' : 'white'} url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'><path d='M3.125 6.16626C4.99121 8.37417 7.04957 10.9183 9.28537 13.1965C9.6443 13.5622 10.2242 13.5863 10.6108 13.25C12.9689 11.199 14.6939 9.17938 16.875 6.36762' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat;
    background-position: right 15px top 50%;
}
  .input.secondary-style {
    border: 1px solid ${this.value ? '#1E007D4D' : '#1E007D'};
  }
  ${
    this.theme === 'outline'
      ? `
    .input {
      background-color: transparent;
      border: 1px solid #1E007D;
      font-weight: ${Font.weight.medium};
    }
  `
      : ''
  }

.input:focus {
    background: ${this.secondaryStyle ? '#BFF0FF' : 'white'} url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'><path d='M16.875 13.8337C15.0088 11.6258 12.9504 9.08166 10.7146 6.80348C10.3557 6.43782 9.77576 6.41373 9.38917 6.75C7.03106 8.80105 5.30608 10.8206 3.125 13.6324' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat;
    background-position: right 15px top 50%;
}

  label {
    font-family: ${Font.family};
    color:  #1E007D;
    font-size: 14px;
    font-weight: ${this.fontWeight ? this.fontWeight : this.secondaryStyle ? Font.weight.medium : Font.weight.bold};
  }
  
  .error {
    background: #FFEBD7;
    border-bottom: 3px solid #FF4600;
  }
  `

  handleInput = (event: Event) => {
    this.value = (event as any).target.value
  }

  eventListeners: EventListeners = [['select', 'change', this.changeSelect]]
}
